import * as THREE from "three";
import Experience from "../Experience.js";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { geomConfig } from "../config/geomConfig.js";
import debug from "debug";
import MakePartsPreprocessor from "./MakePartsPreprocessor.js";
const log = debug("app:mkgroup");

/**
 * The `MakeGroup` class is responsible for creating and managing a Three.js group
 * @category Experience
 * @subcategory World
 */

export default class MakeGroup extends THREE.Group {
  /**
   * Constructs a MakeGroup instance.
   */

  constructor() {
    try {
      super();
      this.experience = new Experience();
      this.eventHub = this.experience.eventHub;
      this.scene = this.experience.scene;
      this.groupXMO = new THREE.Group();
    } catch (error) {
      console.error("Error in MakeGroup constructor:", error);
    }
  }

  cleanup() {
    try {
        if (this.eventHub) {
            this.eventHub.off("cameraYDirectionChanged");
            this.eventHub.off("3d-enabled");
            this.eventHub.off("dark-switch");
        }

        if (this.groupXMO) {
            this.groupXMO.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach((mat) => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
            this.scene.remove(this.groupXMO);
            this.groupXMO.clear();
        }

        if (this.scene) {
            this.scene.traverse((object) => {
                if (object.name === "myGrid" || object.name === "XMO") {
                    this.scene.remove(object);
                    if (object.geometry) object.geometry.dispose();
                    if (object.material) object.material.dispose();
                }
            });
        }

        log("Cleanup completed successfully.");
    } catch (error) {
        console.error("Error during cleanup:", error);
    }
}

  /**
   * Layer depth values for different elements.
   */
  layerZ = { ring: 0.001, circle: 0.002, text: 0.001 };

  /**
   * Initializes the MakeGroup instance.
   * 
   * @param {Object} params - Configuration parameters for the group.
   * @param {THREE.Object3D} shape - The shape to be added to the group.
   * @returns {THREE.Group} - The initialized groupXMO.
   */
  async init(params, shape) {
    try {
      const text = await this.createTextGeometry(params);

      this.initializeGroup(params, shape, text);
      this.createPorts(params, shape);
      const bBox = this.createBoundingBox();
      this.setXMOPosition(params); // Added await here
      this.rotateXMO(params);

      log("rotated", params, "userData", this.groupXMO.userData);
      this.applyUserData(params);

      // Apply current layer transparency to the newly created part
      this.applyCurrentLayerTransparency(params.layer);

      log("set position");

      return this.groupXMO;
    } catch (error) {
      console.error("Error in MakeGroup init:", error);
    }
  }

  /**
   * Initializes the group with its components.
   * 
   * @param {Object} params - Configuration parameters.
   * @param {THREE.Object3D} shape - The shape to be added.
   * @param {Object} text - The text meshes.
   */
  initializeGroup(params, shape, text) {
    try {
      log("this is shape", shape);
      const { layer, w, h, glb, objectId, viewMode } = params;
      const textMesh = text?.textMesh;
      const mfText = text?.mfText;
      this.groupXMO.position.set(0, 0, 0); // Reset position
      this.groupXMO.rotation.set(0, 0, 0); // Reset rotation
      this.groupXMO.scale.set(1, 1, 1); // Reset scale to default (no scaling)
      this.groupXMO.name = "XMO";
      this.groupXMO.add(shape);
      if (textMesh && mfText) {
        this.groupXMO.add(textMesh, mfText);
      }

      if (layer === 50) {
        this.groupXMO.children.forEach((child) => {
          child.position.z += h / 2;
        });
      } else if (layer === 40) {
      } else if (layer === 33) {
        this.groupXMO.children.forEach((child) => {
          child.position.x += w / 2;
          child.position.z += h / 2;
        });
      } else {
        this.groupXMO.children.forEach((child) => {
          child.position.x += w / 2;
          child.position.z += h / 2;
        });
      }

      this.scene.add(this.groupXMO);
    } catch (error) {
      console.error("Error in MakeGroup initializeGroup:", error);
    }
  }

  /**
   * Creates ports for the group.
   * 
   * @param {Object} params - Configuration parameters.
   * @param {THREE.Object3D} shape - The shape to attach ports.
   */
  createPorts(params, shape) {
    try {
      const { ports, w, h } = params;
      ports.forEach((port) => {
        log("port", port);
        const connPoint = new THREE.Group();
        const ringGeometry = new THREE.RingGeometry(0.08, 0.14, 32);
        const circleGeometry = new THREE.CircleGeometry(0.08, 32);
        connPoint.rotation.x = THREE.MathUtils.degToRad(-90);
        const ringMaterial = new THREE.MeshBasicMaterial({
          color: 0x1b1b1b,
          side: THREE.DoubleSide,
        });
        const circleMaterial = new THREE.MeshBasicMaterial({
          color: 0x000000,
          side: THREE.DoubleSide,
        });
        const portX = port.x;
        const portY = -port.y;
        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.name = "ring";
        const circle = new THREE.Mesh(circleGeometry, circleMaterial);
        circle.name = "circle";
        ring.position.set(0, 0, this.layerZ.ring);
        circle.position.set(0, 0, this.layerZ.circle);

        connPoint.position.set(portX, 0, -portY);

        if (port.type === "INP") {
          circleMaterial.color.set("#ffffff");
          connPoint.userData.typeOfPort = "INPUT";
        } else if (port.type === "OUT") {
          circleMaterial.color.set("#000000");
          connPoint.userData.typeOfPort = "OUTPUT";
        } else if (port.type.startsWith("HY")) {
          circleMaterial.color.set("#a0a0a0");
          connPoint.userData.typeOfPort = "HYBRID";
        } else if (port.type === "PRB") {
          circleMaterial.color.set("#000000"); // Black color for PROBE ports
          connPoint.userData.typeOfPort = "PROBE";
        }

        connPoint.add(ring);
        connPoint.add(circle);

        const direction = new THREE.Vector3();
        if (port.y === 0 && port.x !== 0) {
          direction.set(0, 0, -1); // negative Y direction
        } else if (port.x === w && port.y !== 0) {
          direction.set(1, 0, 0); // Positive X direction
        } else if (port.y === h && port.x !== 0) {
          direction.set(0, 0, 1); // Negative Y direction
        } else if (port.x === 0 && port.y !== 0) {
          direction.set(-1, 0, 0); // Negative X direction
        }
        connPoint.direction = direction;
        connPoint.name = "ConnPoint";

        this.groupXMO.add(connPoint);
      });
    } catch (error) {
      console.error("Error in MakeGroup createPorts:", error);
    }
  }

  /**
   * Creates text geometry.
   * 
   * @param {Object} params - Configuration parameters.
   * @returns {Promise<Object>} - The generated text meshes.
   */
  async createTextGeometry(params) {
    try {
      const { w, h, partNumber, mfgPart, layer, viewMode } = params;
      log("layer in text geometry", layer, params);

      if (layer >= 40 && layer != 70) {
        return;
      }

      let xPosition = layer === 10 ? w / 2 - 0.4 : -w / 2 + 0.4;
      let layerZDirection = layer === 10 ? -1 : 1;

      const loader = new FontLoader();

      return new Promise((resolve, reject) => {
        loader.load(
          "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
          (font) => {
            try {
              const textProps = {
                font: font,
                size: 0.15,
                depth: 0.0001,
                curveSegments: 12,
              };
              const textGeometry = new TextGeometry(partNumber, textProps);
              const textGeometryMF = new TextGeometry(mfgPart, textProps);
              const textMaterial = new THREE.MeshBasicMaterial({
                color: 0x000000,
              });
              const textMesh = new THREE.Mesh(textGeometry, textMaterial);
              textMesh.userData.type = "textGeometry";
              textMesh.position.set(
                xPosition,
                this.layerZ.text * layerZDirection,
                -h / 2 + 1.25
              );
              textMesh.rotation.x = THREE.MathUtils.degToRad(-90);
              textMesh.name = "partNumber";

              const mfText = new THREE.Mesh(textGeometryMF, textMaterial);
              mfText.position.set(
                xPosition,
                this.layerZ.text * layerZDirection,
                h / 2 - 1.15
              );
              mfText.userData.type = "textGeometry";
              mfText.rotation.x = THREE.MathUtils.degToRad(-90);
              mfText.name = "MfgPart";

              if (layer === 10) {
                textMesh.rotation.y = THREE.MathUtils.degToRad(180);
                mfText.rotation.y = THREE.MathUtils.degToRad(180);
              }

              // Set visibility based on viewMode
              const isVisible = viewMode.viewMode !== "layout";
              textMesh.visible = isVisible;
              mfText.visible = isVisible;

              resolve({ textMesh, mfText });
            } catch (error) {
              console.error("Error creating text geometry:", error);
              reject(error);
            }
          },
          undefined,
          (error) => {
            console.error("An error occurred loading the font:", error);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error("Error in MakeGroup createTextGeometry:", error);
    }
  }

  /**
   * Creates a bounding box for the group.
   */
  createBoundingBox() {
    try {
      const shape = this.groupXMO.getObjectByName("Shape");

      if (shape) {
        const box = new THREE.Box3().setFromObject(shape);
        const boxSize = new THREE.Vector3();
        box.getSize(boxSize);
        const geometry = new THREE.BoxGeometry(boxSize.x, boxSize.y, boxSize.z);
        const material = new THREE.MeshBasicMaterial({
          color: 0x00ff00,
          transparent: true,
          opacity: 0, // Fully transparent
        });

        const boundingBoxMesh = new THREE.Mesh(geometry, material);
        boundingBoxMesh.name = "bBox";

        const boxCenter = new THREE.Vector3();
        box.getCenter(boxCenter);
        boundingBoxMesh.position.copy(boxCenter);
        boundingBoxMesh.layers.set(20);
        this.groupXMO.add(boundingBoxMesh);
        return boxCenter;
      } else {
        console.warn("Shape not found in groupXMO");
      }
    } catch (error) {
      console.error("Error in MakeGroup createBoundingBox:", error);
    }
  }

  /**
   * Rotates the XMO group based on the given parameters.
   * 
   * @param {Object} params - Rotation parameters.
   */
  async rotateXMO(params) {
    try {
      log("params at rotate", params);
      const { version, angle, layer, partNumber } = params;
      log("version in rotate", version, typeof version);
      if (version === 1) {
        log("angle", angle);
        const makePartsPreprocessor = new MakePartsPreprocessor();
        const { x, z } = await makePartsPreprocessor.init({
          method: "rotate",
          arg: params,
        });
        log("x and z, angle", x, z, angle);
        this.groupXMO.position.x += x;
        this.groupXMO.position.z += z;
      }
      const axis = new THREE.Vector3(0, 1, 0); // Rotate around Y-axis
      const angleInRadians = THREE.MathUtils.degToRad(angle);
      this.groupXMO.rotateOnAxis(axis, angleInRadians);
    } catch (error) {
      console.error("Error in MakeGroup rotateXMO:", error);
    }
  }

  /**
   * Sets the position of the XMO group.
   * 
   * @param {Object} params - Position parameters.
   */
  async setXMOPosition(params) {
    try {
      log("setting XMO position - params", params.layer);
      const { x, z, layer, viewMode } = params;
      log("thisisparams", params);
      const layerConfig = geomConfig.layers.find(
        (config) => config.layer === layer
      );
      log("set XMO position before", this.groupXMO.position);

      if (viewMode["3d"] === true) {
        const y = layerConfig.y * layerConfig.y3;
        log("this is y", y);
        this.groupXMO.position.set(x, y, z);
      } else {
        this.groupXMO.position.set(x, layerConfig.y2, z);
      }
      log("set XMO position after", this.groupXMO.position);
    } catch (error) {
      console.error("Caught an error in setXMOPosition:", error);
    }
  }

  applyUserData(params) {
    try {
      log("params userData", params);
      log("before userData", this.groupXMO.userData);
      this.groupXMO.userData = { ...params };
      this.groupXMO.userData.pn = params.partNumber;
      log("after userData", this.groupXMO.userData);
    } catch (error) {
      console.error("Error in MakeGroup applyUserData:", error);
    }
  }

  /**
   * Gets the current transparency value for a given layer by reading the slider value.
   * @param {number} layer - The layer number.
   * @returns {number} - The opacity value (0-1), defaults to 1 if slider not found.
   */
  getCurrentLayerTransparency(layer) {
    try {
      // Map layer number to slider ID
      const layerToSliderId = {
        10: "Ctl",
        20: "Housing",
        30: "Shape",
        33: "Walls",
        34: "Housing", // Shield layer uses Housing slider
        35: "Probe",
        37: "Housing", // Housings layer uses Housing slider
        40: "GSG",
        50: "GSG",
        60: "Housing",
        70: "Lids",
        90: "Housing", // Ruler layer uses Housing slider (or create separate if needed)
      };

      const sliderId = layerToSliderId[layer];
      if (!sliderId) {
        log(`No slider mapping found for layer ${layer}, defaulting to opacity 1`);
        return 1;
      }

      // Try to get value from SliderTransparency instance if available
      if (this.experience.world?.functions?.sliderTransparency?.sliderValues) {
        const storedValue = this.experience.world.functions.sliderTransparency.sliderValues[sliderId];
        if (storedValue !== undefined) {
          log(`Found transparency value from SliderTransparency for layer ${layer}: ${storedValue}`);
          return storedValue;
        }
      }

      // Fallback: read directly from DOM slider
      const slider = document.getElementById(sliderId);
      if (slider) {
        const rawValue = parseFloat(slider.value) || 0;
        const opacityValue = 0.99 - rawValue / 100;
        log(`Found transparency value from DOM slider for layer ${layer}: ${opacityValue}`);
        return opacityValue;
      }

      log(`Slider not found for layer ${layer}, defaulting to opacity 1`);
      return 1;
    } catch (error) {
      console.error("Error in getCurrentLayerTransparency:", error);
      return 1; // Default to fully opaque on error
    }
  }

  /**
   * Applies the current layer transparency to the newly created part.
   * @param {number} layer - The layer number.
   */
  applyCurrentLayerTransparency(layer) {
    try {
      const opacityValue = this.getCurrentLayerTransparency(layer);
      log(`Applying transparency ${opacityValue} to new part in layer ${layer}`);

      if (this.groupXMO && this.groupXMO.name === "XMO") {
        // Traverse all children recursively to find all meshes
        this.groupXMO.traverse((object) => {
          // Skip bBox from transparency changes
          if (object.name === "bBox" && opacityValue > 0) {
            return;
          }

          if (object.isMesh) {
            // Update material transparency
            if (object.material) {
              const materials = Array.isArray(object.material)
                ? object.material
                : [object.material];
              
              materials.forEach((material) => {
                material.opacity = opacityValue;
                material.transparent = opacityValue < 1;
                material.needsUpdate = true;
              });
            }

            // Handle visibility and raycasting based on opacity
            if (opacityValue <= 0) {
              object.raycast = function () {};
            } else {
              object.raycast = THREE.Mesh.prototype.raycast;
            }
          }
        });

        // Update group visibility
        this.groupXMO.visible = opacityValue > 0;

        log(`Applied transparency ${opacityValue} to new part in layer ${layer}`);
      }
    } catch (error) {
      console.error("Error in applyCurrentLayerTransparency:", error);
    }
  }
}
